import React, { useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { FiEdit, FiPlus } from 'react-icons/fi'
import utility from 'src/utils/utility'
import {
    Box, Flex, Text, tokens,
    Button, Card, BackLink,
    InstitutionTable, TableColumn,
} from 'src/componentv2'
import SurveyApiContext, { SurveyQuestion, SurveyTrigger } from 'src/provider/API/v2/call-service/Survey'
import { NewSurveyTriggerModal } from '../surveyManagement/NewSurveyTriggerModal'
import { NewSurveyQuestionModal } from './NewSurveyQuestionModal'

const PAGE_PADDING_X = '3.2rem'

const QUESTION_TYPE_STYLE: Record<string, { bg: string; color: string; label: string }> = {
    Rating: { bg: '#E0F2FE', color: '#0369A1', label: 'Rating' },
    TrueFalse: { bg: '#ECFDF3', color: '#12B76A', label: 'True False' },
    FreeText: { bg: '#F4F3FF', color: '#5925DC', label: 'Free Text' },
}

const SurveyDetailsPage = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { getSurveyTriggers, getSurveyQuestions } = useContext(SurveyApiContext)

    const params = new URLSearchParams(location.search)
    const institutionCode = params.get('institutionCode') || ''
    const triggerId = Number(params.get('triggerId'))

    const [trigger, setTrigger] = useState<SurveyTrigger | null>(null)
    const [questions, setQuestions] = useState<SurveyQuestion[]>([])
    const [loading, setLoading] = useState(true)
    const [showEditTrigger, setShowEditTrigger] = useState(false)
    const [showQuestionModal, setShowQuestionModal] = useState(false)
    const [editingQuestion, setEditingQuestion] = useState<SurveyQuestion | undefined>(undefined)

    const fetchTrigger = async () => {
        const response = await getSurveyTriggers(institutionCode)
        const match = (response || []).find((item: SurveyTrigger) => item.id === triggerId)
        setTrigger(match ?? null)
    }

    const fetchQuestions = async () => {
        const response = await getSurveyQuestions(institutionCode)
        setQuestions(response || [])
    }

    useEffect(() => {
        (async () => {
            if (!institutionCode || !triggerId) return
            setLoading(true)
            await Promise.all([fetchTrigger(), fetchQuestions()])
            setLoading(false)
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionCode, triggerId])

    const openAddQuestion = () => {
        setEditingQuestion(undefined)
        setShowQuestionModal(true)
    }

    const openEditQuestion = (question: SurveyQuestion) => {
        setEditingQuestion(question)
        setShowQuestionModal(true)
    }

    const columns: TableColumn<SurveyQuestion>[] = [
        {
            key: 'questionText',
            title: 'Question',
            dataIndex: 'questionText',
        },
        {
            key: 'surveyType',
            title: 'Type',
            render: (row) => {
                const style = QUESTION_TYPE_STYLE[row.surveyType] ?? { bg: tokens.border, color: tokens.textMuted, label: row.surveyType }
                return (
                    <Box
                        width="auto" style={{ display: 'inline-block' }}
                        bgColor={style.bg} radius="1.6" paddingX="1" paddingY="0.2"
                    >
                        <Text fontSize="1.2" fontWeight="600" color={style.color}>{style.label}</Text>
                    </Box>
                )
            },
        },
        {
            key: 'actions',
            title: '',
            align: 'right',
            render: (row) => (
                <Button variant="ghost" onClick={() => openEditQuestion(row)}>
                    <FiEdit size={14} style={{ marginRight: '0.4rem' }} />
                    Edit
                </Button>
            ),
        },
    ]

    if (!institutionCode || !triggerId) {
        return (
            <div style={{ padding: `2.4rem ${PAGE_PADDING_X}` }}>
                <Text fontSize="1.4" color={tokens.textMuted}>
                    Missing survey trigger reference. Go back to Survey Management and select a trigger.
                </Text>
            </div>
        )
    }

    return (
        <div style={{ padding: `2.4rem ${PAGE_PADDING_X}` }}>
            <Flex items="center" gap="1.2">
                <BackLink onClick={() => navigate('/dashboardv2/survey-management')}>Survey Management</BackLink>
                <Text fontSize="2.4" fontWeight="700" color={tokens.textDark}>Survey Details</Text>
            </Flex>

            {loading ? (
                <Text topMargin="2.4" fontSize="1.4" color={tokens.textMuted}>Loading survey details...</Text>
            ) : !trigger ? (
                <Text topMargin="2.4" fontSize="1.4" color={tokens.textMuted}>Survey trigger not found.</Text>
            ) : (
                <>
                    <Card padding="2rem" radius="1.2" borderColor={tokens.border} shadow={tokens.shadowCard} topMargin="2.4">
                        <Flex justify="space-between" items="flex-start" wrap="wrap" gap="2.4">
                            <Flex gap="4" wrap="wrap">
                                <Box>
                                    <Text fontSize="1.3" fontWeight="700" color={tokens.textValue}>Survey Name</Text>
                                    <Text topMargin="0.4" fontSize="1.4" color={tokens.textLabel}>{trigger.triggerName}</Text>
                                </Box>
                                <Box>
                                    <Text fontSize="1.3" fontWeight="700" color={tokens.textValue}>Description</Text>
                                    <Text topMargin="0.4" fontSize="1.4" color={tokens.textLabel}>{trigger.triggerDescription}</Text>
                                </Box>
                                <Box>
                                    <Text fontSize="1.3" fontWeight="700" color={tokens.textValue}>Interval</Text>
                                    <Text topMargin="0.4" fontSize="1.4" color={tokens.textLabel}>{trigger.surveyInterval} days</Text>
                                </Box>
                            </Flex>
                            <Button variant="ghost" onClick={() => setShowEditTrigger(true)}>
                                <FiEdit size={14} style={{ marginRight: '0.6rem' }} />
                                Edit Survey Details
                            </Button>
                        </Flex>
                    </Card>

                    <Flex justify="space-between" items="center" topMargin="3.2">
                        <Text fontSize="1.8" fontWeight="700" color={tokens.textDark}>Questions</Text>
                        <Button onClick={openAddQuestion}>
                            <FiPlus size={16} style={{ marginRight: '0.6rem' }} />
                            Add Question
                        </Button>
                    </Flex>

                    <Box topMargin="1.6" bgColor={tokens.white} borderColor={tokens.border} radius="1.2" shadow={tokens.shadowCard} style={{ overflow: 'hidden' }}>
                        <InstitutionTable columns={columns} data={questions} emptyText="No questions added yet." />
                    </Box>
                </>
            )}

            <NewSurveyTriggerModal
                open={showEditTrigger}
                onClose={() => setShowEditTrigger(false)}
                ibInstitutionCode={institutionCode}
                isEdit
                initialTrigger={trigger ?? undefined}
                onSaved={fetchTrigger}
            />

            <NewSurveyQuestionModal
                open={showQuestionModal}
                onClose={() => setShowQuestionModal(false)}
                ibInstitutionCode={institutionCode}
                surveyTriggerId={triggerId}
                isEdit={!!editingQuestion}
                initialQuestion={editingQuestion}
                onSaved={fetchQuestions}
            />
        </div>
    )
}

export default utility.routeData('survey-details', 'SurveyDetails', <SurveyDetailsPage />)
